import {
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  PARENT_CUSTOM_OFFICE_TYPE,
  PARENT_CUSTOM_SUBCATEGORY_TYPE,
  PARENT_CUSTOM_WORKSPACE_TYPE,
  PRIVATE_OFFICES_SUBCATEGORY,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import React, { useCallback, useMemo } from 'react';
import {
  ISpaceType,
  ISpaceTypeCategory,
  ISpaceTypeSubcategory,
} from 'spw-redux/modules/space_types/types';
import { useSelector } from 'react-redux';
import {
  selectProjectSpaceTypeCategories,
  selectSpaceTypeCategoriesMap,
} from 'spw-redux/modules/projects/selectors';
import { SpaceTypeCategoryMap } from 'spw-redux/modules/floorplan/types';

const parentsBySubcategory = {
  [PRIVATE_OFFICES_SUBCATEGORY]: PARENT_CUSTOM_OFFICE_TYPE,
  [WORKSTATIONS_SUBCATEGORY]: PARENT_CUSTOM_WORKSPACE_TYPE,
  [CUSTOM_SUBCATEGORY]: PARENT_CUSTOM_SUBCATEGORY_TYPE,
};

type Props = {
  setSelectedParentSpaceType: React.Dispatch<React.SetStateAction<ISpaceType>>;
};

export const useCustomSpaceTypes = ({ setSelectedParentSpaceType }: Props) => {
  const spaceTypeCategoriesMap = useSelector(selectSpaceTypeCategoriesMap);

  const getCustomSpaceTypeForIWS = useCallback(
    (
      spaceTypeCategory: ISpaceTypeCategory,
      subcategory: ISpaceTypeSubcategory
    ) => {
      // TODO: Remove this once custom space types table has been removed
      const subcategorySpaceType = parentsBySubcategory[subcategory.category];

      if (subcategory.category === CUSTOM_SUBCATEGORY) {
        return spaceTypeCategoriesMap[subcategory.category].spaceTypes.find(
          (st) => st.spaceType === subcategorySpaceType
        );
      }

      return spaceTypeCategoriesMap[spaceTypeCategory.category].spaceTypes.find(
        (st) => st.spaceType === subcategorySpaceType
      );
    },
    [spaceTypeCategoriesMap]
  );

  const getCustomSpaceTypeForCategory = useCallback(
    (category: ISpaceTypeCategory) => {
      return spaceTypeCategoriesMap[category.category]?.spaceTypes.find((st) =>
        st.spaceType.startsWith('custom')
      );
    },
    [spaceTypeCategoriesMap]
  );
  const selectParentSpaceType = useCallback(
    (
      spaceTypeCategory: ISpaceTypeCategory,
      subcategory?: ISpaceTypeSubcategory
    ) => {
      if (
        spaceTypeCategory.category === INDIVIDUAL_WORKSPACES_CATEGORY &&
        subcategory
      ) {
        setSelectedParentSpaceType(
          getCustomSpaceTypeForIWS(spaceTypeCategory, subcategory)
        );
      } else {
        setSelectedParentSpaceType(
          getCustomSpaceTypeForCategory(spaceTypeCategory)
        );
      }
    },
    [
      getCustomSpaceTypeForIWS,
      getCustomSpaceTypeForCategory,
      setSelectedParentSpaceType,
    ]
  );

  return {
    selectParentSpaceType,
  };
};

export const useSpaceTypeDialogTitle = (parentSpaceType: ISpaceType) => {
  const projectSpaceTypeCategories = useSelector(
    selectProjectSpaceTypeCategories
  );

  const spaceTypeCategories: SpaceTypeCategoryMap = useSelector(
    selectSpaceTypeCategoriesMap
  );

  const parentProjectSpaceTypeCategory = useMemo(() => {
    if (!parentSpaceType) return undefined;

    const parentSpaceTypeCategory = Object.values(spaceTypeCategories).find(
      (stc) => {
        return (
          stc.spaceTypes.findIndex(
            (st) => st.spaceType === parentSpaceType.spaceType
          ) !== -1
        );
      }
    );

    return projectSpaceTypeCategories.find((pstc) => {
      if (parentSpaceTypeCategory.category === CUSTOM_SUBCATEGORY) {
        return (
          pstc.defaultSpaceTypeCategory.category ===
          INDIVIDUAL_WORKSPACES_CATEGORY
        );
      }
      return (
        pstc.defaultSpaceTypeCategory.category ===
        parentSpaceTypeCategory.category
      );
    });
  }, [spaceTypeCategories, parentSpaceType, projectSpaceTypeCategories]);

  return useMemo(() => {
    if (!parentSpaceType) return '';

    if (parentSpaceType.spaceType === PARENT_CUSTOM_OFFICE_TYPE)
      return 'Custom Private Office';

    if (parentSpaceType.spaceType === PARENT_CUSTOM_WORKSPACE_TYPE)
      return 'Custom Workstation';

    if (parentSpaceType.spaceType === PARENT_CUSTOM_SUBCATEGORY_TYPE) {
      const customSubcategory = parentProjectSpaceTypeCategory.subcategories.find(
        (subcategory) =>
          subcategory.defaultSpaceTypeCategory.category === CUSTOM_SUBCATEGORY
      );

      return `Custom ${customSubcategory?.name}`;
    }

    return parentProjectSpaceTypeCategory &&
      parentProjectSpaceTypeCategory.defaultSpaceTypeCategory.category !==
        INDIVIDUAL_WORKSPACES_CATEGORY
      ? `Custom ${parentProjectSpaceTypeCategory.name}`
      : parentSpaceType.name;
  }, [parentProjectSpaceTypeCategory, parentSpaceType]);
};
